import logger from '@eventbrite/client-logger';
import { getWindowObject } from '@eventbrite/feature-detection';
import { CustomSubmissionError } from '@eventbrite/redux-form-tools';
import { OrderDocumentStatus } from '../api/constants';
import {
    getFormValues,
    getAnswersForQuestionsAffectingQuebecQSTTaxes,
} from '../selectors/forms';
import { getUserId, isUserLoggedIn } from '../selectors/user';
import { hasSelectedRedirectedPaymentMethod } from '../selectors/order/hasSelectedRedirectedPaymentMethod';
import {
    getIsNosSourceOfTruth,
    getOrderId,
    getOrderPersistentId,
} from '../selectors/order/typedOrderSelectors';
import { logout } from './auth';
import {
    getAndSaveAvailablePaymentMethods,
    getOrganizerData,
    getOrderSuccessPromise,
    requestExternalPaymentWithNosWorkflow,
    requestPayment as requestOrderPayment,
    updateEmailPreferencesForOrderOwner,
    updateLoggedInUserIfChanged,
} from './order/common';
import { getSurveyInfoAndPrefillFieldsAction } from './survey';
import { placeOrder } from './order/place';
import { requestPayment as requestPayInPayment } from './payIn/common';
import { placePayIn } from './payIn/place';
import { initializeEBMarketingOptIn } from './marketingOptIn';
import { initializeSendEmailToAttendees } from './sendEmailToAttendees';
import { initializeTaxesSurveyQuestions } from './taxesSurveyQuestions';
import { getSurvey } from '../selectors/survey';
import { handleOrderError } from './order/utils';
import { waitUntilOrderIsProcessed } from './order/polling';
import { isQAEnv, isSTAGEEnv } from '../utils/browser';
import { CHECKOUT_FORM_NAME } from '../constants';
import { clearPaymentMethods as clearPaymentMethodsApi } from '../api/order';
import { isTicketSelectionFullSizePage } from '../selectors/routing';
import { handleOrderErrorInTicketSelectionPage } from '../actions/order/utils';
import { getEventId } from '../selectors/event';
import {
    updateOrderStatusAction,
    setHasExternalPaymentFirstCallCompletedAction,
} from './order';
import { TLZEnvironment } from '../models/bff';
import { PaymentMethodType } from '../models/paymentMethods/enums';
import { updatePaymentHistory } from '../actions/paymentHistory';

const _handleInconsistentUser = (dispatch, getState) => {
    const state = getState();

    if (isUserLoggedIn(state)) {
        // If user is now logged in, fetch payment methods
        dispatch(getAndSaveAvailablePaymentMethods());
        dispatch(getSurveyInfoAndPrefillFieldsAction());
    } else {
        // Otherwise, clear payment methods and buyer info
        dispatch(logout());
    }
};

export const ensureConsistentUser = () => async (dispatch, getState) => {
    const userIdBeforeConsistencyCheck = getUserId(getState());
    await updateLoggedInUserIfChanged(dispatch, getState);

    const userIdAfterConsistencyCheck = getUserId(getState());
    const hasUserChanged =
        userIdBeforeConsistencyCheck !== userIdAfterConsistencyCheck;
    if (hasUserChanged) {
        _handleInconsistentUser(dispatch, getState);
    }

    return hasUserChanged;
};

export const submitCheckoutForm =
    ({ formData }) =>
    async (dispatch, getState) => {
        const isNosSourceOfTruth = getIsNosSourceOfTruth(getState());
        const isTicketSelectionPage = isTicketSelectionFullSizePage(getState());
        const formDataToSubmit =
            formData ?? getFormValues(getState(), CHECKOUT_FORM_NAME);
        try {
            if (isNosSourceOfTruth) {
                return await _submitCheckoutWithNosAsyncWorkflow({
                    formData: formDataToSubmit,
                    dispatch,
                    getState,
                });
            }

            return _submitCheckoutWithLosSyncWorkflow({
                formData: formDataToSubmit,
                dispatch,
                getState,
            });
        } catch (error) {
            if (isTicketSelectionPage) {
                handleOrderErrorInTicketSelectionPage(dispatch);
            }

            throw error;
        }
    };

export const submitPayInForm = (formData) => (dispatch, getState) => {
    const formDataCleaned = getSurvey(
        getState(),
    ).payinsSurveySettings.getCleanedFormData(formData);
    const payIn = dispatch(placePayIn(formDataCleaned));
    // This action MUST return the last dispatch, if not, reduxForm will set the form as submitSuccess, and will not show errors
    // this ensures that reduxForm waits until the submit is finished to update the form status with the correct result.
    return dispatch(requestPayInPayment(formDataCleaned.paymentMethod, payIn));
};

export const initializeCheckoutForm = () => (dispatch) => {
    dispatch(initializeSendEmailToAttendees());
    dispatch(initializeEBMarketingOptIn());
    dispatch(initializeTaxesSurveyQuestions());
    dispatch(getSurveyInfoAndPrefillFieldsAction());
    dispatch(getOrganizerData());
};

const _submitCheckoutWithLosSyncWorkflow = ({
    formData,
    dispatch,
    getState,
}) => {
    const state = getState();
    const {
        hasQuebecQSTTaxQuestion,
        prePaymentSurveySettings: { getCleanedFormData },
    } = getSurvey(state);

    let formDataCleaned = getCleanedFormData(formData);
    // Only in Quebec QST we have to send the billing zipcode as it's used to tell whether buyer is from Quebec
    // (and should be charged) or not
    if (hasQuebecQSTTaxQuestion) {
        formDataCleaned = {
            ...formDataCleaned,
            buyer: {
                ...formDataCleaned.buyer,
                ...getAnswersForQuestionsAffectingQuebecQSTTaxes(
                    formData,
                    state,
                ),
            },
        };
    }

    const order = dispatch(
        placeOrder({
            formData: formDataCleaned,
        }),
    );
    const paymentPromise = dispatch(
        requestOrderPayment(formDataCleaned.paymentMethod, order),
    );

    // This action MUST return the last dispatch, if not, reduxForm will set the form as submitSuccess, and will not show errors
    // this ensures that reduxForm waits until the submit is finished to update the form status with the correct result.
    return dispatch(updateEmailPreferencesForOrderOwner(paymentPromise));
};

const _submitCheckoutWithNosAsyncWorkflow = async ({
    formData,
    dispatch,
    getState,
}) => {
    try {
        const stateBeforePlacingOrder = getState();
        const {
            hasQuebecQSTTaxQuestion,
            prePaymentSurveySettings: { getCleanedFormData },
        } = getSurvey(stateBeforePlacingOrder);
        const baseFormDataCleaned = getCleanedFormData(formData);
        const env = stateBeforePlacingOrder.app.isProd
            ? TLZEnvironment.prod
            : isSTAGEEnv(getWindowObject('location')?.host)
            ? TLZEnvironment.preprod
            : isQAEnv(getWindowObject('location')?.host)
            ? TLZEnvironment.qa
            : TLZEnvironment.dev;
        const formDataCleaned =
            // Only in Quebec QST we have to send the billing zipcode as it's used to tell whether buyer is from Quebec
            // (and should be charged) or not
            hasQuebecQSTTaxQuestion
                ? {
                      ...baseFormDataCleaned,
                      buyer: {
                          ...baseFormDataCleaned.buyer,
                          ...getAnswersForQuestionsAffectingQuebecQSTTaxes(
                              baseFormDataCleaned,
                              stateBeforePlacingOrder,
                          ),
                      },
                  }
                : baseFormDataCleaned;

        if (stateBeforePlacingOrder.order.hasRetryPaymentError) {
            await clearPaymentMethodsApi(getOrderId(stateBeforePlacingOrder));
        }
        const order = dispatch(
            placeOrder({
                formData: formDataCleaned,
            }),
        );
        await order.saveAndPlacePromise;

        const stateAfterPlacingOrder = getState();

        const onRedirectionInstructionsAvailable = (
            redirectionInstructions,
        ) => {
            dispatch(
                requestExternalPaymentWithNosWorkflow(
                    formDataCleaned.paymentMethod,
                    { ...order, redirectionInstructions },
                ),
            );
        };

        const onPaymentStatusAvailable = (paymentStatus) => {
            dispatch(
                updatePaymentHistory({
                    payment_status: paymentStatus,
                }),
            );
        };

        const orderStatus = await waitUntilOrderIsProcessed({
            env,
            eventId: getEventId(stateAfterPlacingOrder),
            hasSelectedRedirectedPaymentMethod:
                hasSelectedRedirectedPaymentMethod(stateAfterPlacingOrder),
            onPaymentStatusAvailable,
            orderId: getOrderPersistentId(stateAfterPlacingOrder),
            onRedirectionInstructionsAvailable,
        });

        dispatch(updateOrderStatusAction(orderStatus));

        switch (orderStatus) {
            case OrderDocumentStatus.finalized:
                const orderSuccessPromise = dispatch(getOrderSuccessPromise());

                // This action MUST return the last dispatch, if not, reduxForm will set the form as submitSuccess, and will not show errors
                // this ensures that reduxForm waits until the submit is finished to update the form status with the correct result.
                return dispatch(
                    updateEmailPreferencesForOrderOwner(orderSuccessPromise),
                );

            case OrderDocumentStatus.draft:
                dispatch(setHasExternalPaymentFirstCallCompletedAction(false));

                // TODO: EB-195571 - We will use a specific error message here
                logger.error(
                    new Error(
                        'Payment failed, buyer can retry payment with a different payment method',
                    ),
                );

                throw new CustomSubmissionError(
                    {
                        _error: 'There were errors with your payment. Please review your payment details',
                    },
                    {
                        parsedError: {
                            error: 'RETRY_ERROR',
                            shouldStopSubmit:
                                formData.paymentMethod ===
                                PaymentMethodType.STRIPE_CONFIRMATION_TOKEN,
                        },
                    },
                );
            case OrderDocumentStatus.cancelled:
                dispatch(setHasExternalPaymentFirstCallCompletedAction(false));

                logger.error(new Error('Payment declined'));

                throw new CustomSubmissionError(
                    {
                        _error: 'Your payment was declined',
                    },
                    {},
                );

            default:
                dispatch(setHasExternalPaymentFirstCallCompletedAction(false));

                logger.error(
                    new Error(`Unexpected order status: ${orderStatus}`),
                );

                throw new CustomSubmissionError(
                    {
                        _error: 'There was a problem submitting your order. Please try again.',
                    },
                    {},
                );
        }
    } catch (error) {
        handleOrderError(dispatch, error);

        throw error;
    }
};
